import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import minMax from 'dayjs/plugin/minMax'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { FieldError, FieldErrors } from 'react-hook-form'

import { Asset, AssetContentType } from '@/lib/features/Assets/interfaces'
import {
  PostSharesPayload,
  PostShareValidatePayload,
  SelectableShareClass,
  Share,
  ShareClass,
  SinglePostSharesPayload,
} from '@/lib/features/SmartShares/interfaces'

import messages from './ShareAssetModal.messages'
import { AssetRowFormState, ShareFormState } from './ShareAssetModal.types'

/** Tooltip on a dropdown option that the delegate flow has made unselectable. */
export const DELEGATION_BLOCKED_TOOLTIP = 'Only vehicles can be delegated'

/** Row-level error when the asset picked for a delegation is not a vehicle. */
export const DELEGATION_BLOCKED_ERROR = 'Only vehicles can be delegated, not portable trackers'

/**
 * Delegation exists to let a contact assign the asset to one of their tasks, and tasks take
 * vehicles only — so a portable tracker can never be delegated, however it is reached.
 */
export const isDelegableAsset = (asset: Pick<Asset, 'contentType'> | null | undefined) =>
  asset?.contentType === AssetContentType.VEHICLE

/**
 * True when the share class rules out this asset. Standard Smart Shares carry no such
 * restriction — a portable tracker is perfectly shareable.
 */
export const isAssetBlockedByShareClass = (
  asset: Pick<Asset, 'contentType'> | null | undefined,
  shareClass?: SelectableShareClass | null,
) => shareClass === ShareClass.DELEGATION && !isDelegableAsset(asset)

/** Tooltip on the onward share action when the share it would pass on is a delegation. */
export const ONWARD_SHARE_BLOCKED_TOOLTIP = 'A delegated vehicle cannot be shared onward'

/** Row-level error when the share being passed on is a delegation. */
export const ONWARD_SHARE_BLOCKED_ERROR =
  'A delegated vehicle cannot be shared onward. Ask the owner to share it directly.'

/** Tooltip on the extend action when the share it would extend is a delegation. */
export const EXTEND_BLOCKED_TOOLTIP = 'A delegated vehicle cannot be extended'

/** Row-level error when the share being extended is a delegation. */
export const EXTEND_BLOCKED_ERROR =
  'A delegated vehicle cannot be extended. Delegate it again for the new period.'

/**
 * A delegation lends a vehicle for the receiver's own tasks. It is a fixed grant rather than a
 * share that can be grown or passed along, so neither extending nor onward sharing it is allowed.
 * The backend rejects both; blocking here means the user finds out before filling the form in.
 */
export const isDelegatedShare = (share: Pick<Share, 'shareClass'> | null | undefined): boolean =>
  share?.shareClass === ShareClass.DELEGATION

/** True when this share cannot be passed on again. */
export const isOnwardShareBlocked = (
  share: Pick<Share, 'shareClass'> | null | undefined,
): boolean => isDelegatedShare(share)

/** True when this share cannot be extended. */
export const isExtendBlocked = (share: Pick<Share, 'shareClass'> | null | undefined): boolean =>
  isDelegatedShare(share)

/**
 * The reason a share flow is not allowed to proceed, or undefined when it is.
 *
 * The single place the rule lives, so the table's disabled action, the modal's row error and the
 * payload builders below cannot drift apart — each asks this rather than re-deriving it.
 */
export const getBlockedShareFlowReason = ({
  shareToExtend,
  onwardShareToExtend,
}: {
  shareToExtend?: Pick<Share, 'shareClass'> | null
  onwardShareToExtend?: Pick<Share, 'shareClass'> | null
}): string | undefined => {
  if (isExtendBlocked(shareToExtend)) {
    return EXTEND_BLOCKED_ERROR
  }
  if (isOnwardShareBlocked(onwardShareToExtend)) {
    return ONWARD_SHARE_BLOCKED_ERROR
  }
  return undefined
}

/**
 * Last line of defence before a request is built. The table disables the action and the modal
 * shows a row error, but a payload for a blocked flow must never be constructible — so this
 * throws rather than quietly returning something the API would reject.
 */
export const assertShareFlowAllowed = (context: {
  shareToExtend?: Pick<Share, 'shareClass'> | null
  onwardShareToExtend?: Pick<Share, 'shareClass'> | null
}): void => {
  const reason = getBlockedShareFlowReason(context)
  if (reason) {
    throw new Error(reason)
  }
}

export const getCost = (dailyCost: number, from?: string | null, to?: string | null) => {
  const diff = from && to ? dayjs(to).diff(from, 'days') : 0
  const cost = dailyCost * (diff + 1)

  return cost
}

export const getTotalCost = (dailyCost: number, assets: Array<AssetRowFormState>) =>
  assets
    .map(({ date }) => getCost(dailyCost, date?.from, date?.to))
    .reduce((sum, cost) => sum + cost, 0)

/**
 * Describes which flow the share modals are rendering, so the copy can follow the
 * share class the user picked in the first step.
 */
export type ShareFlowContext = {
  isExtend?: boolean
  isOnwardShare?: boolean
  shareClass?: ShareClass | null
}

export const getAssetsStepTitle = ({ isExtend, isOnwardShare, shareClass }: ShareFlowContext) => {
  if (isExtend) {
    return messages.extendModalTitle.defaultMessage
  }
  if (isOnwardShare) {
    return messages.onwardShareModalTitle.defaultMessage
  }
  if (shareClass === ShareClass.DELEGATION) {
    return messages.delegateModalTitle.defaultMessage
  }

  return messages.title.defaultMessage
}

export const getAssetsStepSubtitle = ({
  isExtend,
  isOnwardShare,
  shareClass,
}: ShareFlowContext) => {
  if (isExtend) {
    return messages.extendShareSubtitle.defaultMessage
  }
  if (!isOnwardShare && shareClass === ShareClass.DELEGATION) {
    return messages.delegateSubtitle.defaultMessage
  }

  return messages.subtitle.defaultMessage
}

export const getContactStepTitle = ({ isExtend, isOnwardShare, shareClass }: ShareFlowContext) => {
  if (isExtend) {
    return messages.extendContactSelectModalTitle.defaultMessage
  }
  if (isOnwardShare) {
    return messages.onwardShareContactSelectModalTitle.defaultMessage
  }
  if (shareClass === ShareClass.DELEGATION) {
    return messages.delegateContactSelectModalTitle.defaultMessage
  }

  return messages.contactSelectModalTitle.defaultMessage
}

/**
 * The share class step only fronts a brand new share. Extending an existing Smart Share or
 * onward sharing an asset already has its own path, so those flows skip the step.
 */
export const shouldSelectShareClass = ({ isExtend, isOnwardShare, shareClass }: ShareFlowContext) =>
  !isExtend && !isOnwardShare && !shareClass

export const adjustDateFromAndTo = (
  newDateFrom: string | undefined,
  newDateTo: string | undefined,
  shareToExtend?: Share,
) => {
  // `_app` registers these too, but the share date maths must also hold when this util is
  // called on its own, outside the app entry. `extend` is idempotent.
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(minMax)
  dayjs.extend(isSameOrBefore)

  // If dateFrom set to today then change it to now
  // Else set dateFrom to start of the chosen date
  let dateFrom = dayjs
    .max(dayjs(), dayjs(newDateFrom))
    .tz('Africa/Johannesburg')
    .format('YYYY-MM-DDTHH:mm:ss')

  // Set dateTo to end of chosen date
  const dateTo = dayjs(newDateTo)
    .tz('Africa/Johannesburg')
    .endOf('day')
    .format('YYYY-MM-DDTHH:mm:ss')

  // If we're extending a share that is already active then dateFrom may not change
  if (shareToExtend && dayjs(shareToExtend.dateFrom).isSameOrBefore(dayjs(), 'date')) {
    dateFrom = shareToExtend.dateFrom
  }

  return {
    dateFrom,
    dateTo,
  }
}

export type SharePayloadContext = {
  assets: Array<AssetRowFormState>
  organizationTo: ShareFormState['organizationTo']
  shareToExtend?: Share
  onwardShareToExtend?: Share
  /**
   * Only set for a brand new share. Extend and onward share flows never choose a class, so the
   * field is left off the payload and the API default (`EXPLICIT`) applies.
   */
  shareClass?: SelectableShareClass | null
}

/**
 * The validation endpoint takes the share class too: overlapping delegation shares are a hard
 * error, where overlapping standard shares to a different organisation are only a warning.
 */
export const convertValidatePayload = ({
  assets,
  organizationTo,
  shareToExtend,
  shareClass,
  acceptWarning,
}: Omit<SharePayloadContext, 'onwardShareToExtend'> & {
  acceptWarning: boolean
}): Array<PostShareValidatePayload> => {
  assertShareFlowAllowed({ shareToExtend })

  return assets.map((rowState) => {
    const { asset } = rowState
    const { dateFrom, dateTo } = adjustDateFromAndTo(
      rowState.date?.from,
      rowState.date?.to,
      shareToExtend,
    )

    const payload = {
      asset,
      dateFrom,
      dateTo,
      organizationTo,
      shareId: shareToExtend?.id,
      acceptWarning,
    } as PostShareValidatePayload

    if (shareClass) {
      payload.shareClass = shareClass
    }

    return payload
  })
}

export const convertToPayload = ({
  assets,
  organizationTo,
  shareToExtend,
  onwardShareToExtend,
  shareClass,
}: SharePayloadContext): PostSharesPayload => {
  assertShareFlowAllowed({ shareToExtend, onwardShareToExtend })

  return assets.map((rowState) => {
    const { asset, reference, contactTo, contactFrom } = rowState
    const { dateFrom, dateTo } = adjustDateFromAndTo(
      rowState.date?.from,
      rowState.date?.to,
      shareToExtend,
    )

    const payload = {
      asset,
      dateFrom,
      dateTo,
      organizationTo,
      reference,
      contactTo,
      contactFrom,
    } as SinglePostSharesPayload

    if (onwardShareToExtend) {
      payload.parentShare = onwardShareToExtend.id
    }

    if (shareClass) {
      payload.shareClass = shareClass
    }

    return payload
  })
}

/** One line in the error summary at the top of the share form. */
export type ShareFormErrorItem = {
  /** Stable key for the list item. */
  key: string
  /** 1-based row number, when the error belongs to a single asset row. */
  row?: number
  message: string
}

/** The asset row fields that can carry an error, in the order they read on screen. */
const ASSET_ROW_ERROR_FIELDS = ['asset', 'date', 'reference'] as const

/**
 * Flattens everything currently wrong with the share form into one ordered list.
 *
 * The summary is the only error banner the form renders, so it has to carry everything: row
 * errors show inline as well, but a row scrolled out of view would otherwise take its error
 * with it.
 *
 * The general (API) error leads, then each row in order. Row errors keep their row number so the
 * reader can find the offending row without scrolling the list of assets.
 */
export const collectShareFormErrors = (
  errors: FieldErrors<ShareFormState>,
  generalError?: string,
): Array<ShareFormErrorItem> => {
  const items: Array<ShareFormErrorItem> = []

  if (generalError) {
    items.push({ key: 'general', message: generalError })
  }

  const organizationToMessage = errors.organizationTo?.message
  if (organizationToMessage) {
    items.push({ key: 'organizationTo', message: organizationToMessage })
  }

  // `errors.assets` is a row array, but RHF also allows an error on the array itself, so it is
  // only iterable once narrowed.
  const assetRowErrors = Array.isArray(errors.assets) ? errors.assets : []

  assetRowErrors.forEach((rowErrors, index) => {
    if (!rowErrors) {
      return
    }

    ASSET_ROW_ERROR_FIELDS.forEach((field) => {
      const message = (rowErrors[field] as FieldError | undefined)?.message
      if (message) {
        items.push({ key: `assets.${index}.${field}`, row: index + 1, message })
      }
    })
  })

  return items
}
